import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { NoticeSessionMessageType } from '@models/planning/notice-session-message.model'
import { NoticeSessionRepresentation } from '@models/planning/notice-session-representation.model'
import {
	SupplierEnrollmentNotice,
	SupplierEnrollmentNoticeAttachment,
	SupplierEnrollmentNoticeAttachmentType,
	SupplierEnrollmentNoticeCounterargument,
	SupplierEnrollmentNoticeCounterargumentDataType,
	SupplierEnrollmentNoticeCounterargumentSearch,
	SupplierEnrollmentNoticeMessageType,
	SupplierEnrollmentNoticeStatus
} from '@models/supplier/notice/supplier-enrollment-notice.model'
import { SupplierEnrollmentNoticeCounterargumentService } from '@services/supplier-enrollment-notice-counterargument.service'
import { SupplierEnrollmentNoticeService } from '@services/supplier-enrollment-notice.service'
import moment from 'moment/moment'
import { EMPTY, Observable, catchError, concatMap, expand, from, map, of, switchMap, toArray } from 'rxjs'

export type SupplierEnrollmentNoticeMessageSender = 'SISTEMA' | 'PARTICIPANTE' | 'AGENTE'

export interface SupplierEnrollmentNoticeMessageItem {
	id: string
	created: string
	submitted?: boolean
	hasAttachment: boolean
	customMessage: string
	resourceId?: string
	sourceTag: string
	senderTag: string
	senderType: SupplierEnrollmentNoticeMessageSender
}

@Injectable({
	providedIn: 'root'
})
export class SupplierEnrollmentNoticeMessageService {
	constructor(
		private supplierEnrollmentNoticeService: SupplierEnrollmentNoticeService,
		private supplierEnrollmentNoticeCounterargumentService: SupplierEnrollmentNoticeCounterargumentService
	) {}

	list(enrollmentId: string): Observable<SupplierEnrollmentNoticeMessageItem[]> {
		if (!enrollmentId) {
			return of([])
		}

		return this.supplierEnrollmentNoticeService.detail(enrollmentId).pipe(
			switchMap(detail => this.listOf(detail)),
			catchError(error => {
				console.error(error)
				return of([])
			})
		)
	}

	/**
	 * As mensagens de uma inscrição já carregada, sem pedir o detalhe. No portal do fornecedor a inscrição vem da listagem
	 * da sessão, que traz mensagens, diligências e anexos de todos os participantes (com a identidade redigida durante a
	 * disputa); o detalhe de um concorrente só responde ao dono e à Administração do órgão (desde 04/10/2026).
	 */
	listOf(enrollment: SupplierEnrollmentNotice): Observable<SupplierEnrollmentNoticeMessageItem[]> {
		if (!enrollment?.id) {
			return of([])
		}

		return this.fetchAllCounterarguments(enrollment.id, enrollment.noticeSession?.id).pipe(
			map(counterarguments => this.mountMessages(enrollment, counterarguments)),
			catchError(error => {
				console.error(error)
				return of([])
			})
		)
	}

	private fetchAllCounterarguments(enrollmentId: string, noticeSessionId?: string) {
		if (!enrollmentId) {
			return of([])
		}

		const query = new SupplierEnrollmentNoticeCounterargumentSearch({ enrollmentId, noticeSessionId })
		let page = 0

		return this.supplierEnrollmentNoticeCounterargumentService.search(query, new HttpParams().set('page', page)).pipe(
			expand(response => {
				if (response.last) {
					return EMPTY
				}

				page += 1
				return this.supplierEnrollmentNoticeCounterargumentService.search(query, new HttpParams().set('page', page))
			}),
			map(response => response.content),
			concatMap(content => from(content)),
			toArray(),
			catchError(error => {
				console.error(error)
				return of([])
			})
		)
	}

	private mountMessages(detail: SupplierEnrollmentNotice, counterarguments: SupplierEnrollmentNoticeCounterargument[]) {
		const items: SupplierEnrollmentNoticeMessageItem[] = []
		const documentationAttachment = this.findDocumentationAttachment(detail)

		if (detail.attachment && documentationAttachment?.created) {
			items.push(
				this.toMessageItem({
					id: `attachment-${documentationAttachment.id}`,
					created: documentationAttachment.created,
					hasAttachment: documentationAttachment.hasAttachment,
					resourceId: documentationAttachment.id,
					sourceTag: 'Anexos',
					senderType: 'SISTEMA',
					title: 'Documentação enviada',
					message: 'Documento de habilitação anexado pelo participante e registrado no sistema.'
				})
			)
		}

		;(detail.representations ?? []).forEach(representation => {
			if (representation?.message && representation?.created) {
				items.push(
					this.toMessageItem({
						id: `representation-${representation.id}`,
						created: representation.created,
						hasAttachment: false,
						sourceTag: 'Diligência',
						senderType: 'SISTEMA',
						title: 'Solicitação de diligência',
						message: representation.message
					})
				)
			}

			this.mapRepresentationAnswers(representation).forEach(answer => items.push(answer))
		})

		;(detail.messages?.data ?? []).forEach(message => {
			if (!message?.message) {
				return
			}

			const statusApproved = detail.status === SupplierEnrollmentNoticeStatus.APPROVED
			const typeAppeal = message.type === SupplierEnrollmentNoticeMessageType.APPEAL
			const replaceAppealApprovedContent = statusApproved && typeAppeal
			const resourceGrantedMessage = (message.message ?? '').trim().toLowerCase() === 'o recurso foi deferido'

			items.push(
				this.toMessageItem({
					id: `notice-message-${message.id}`,
					created: message.created,
					hasAttachment: message.hasAttachment,
					resourceId: message.id,
					sourceTag: 'Julgamento',
					senderType: 'SISTEMA',
					customMessage: replaceAppealApprovedContent ? undefined : message.customMessage,
					title: replaceAppealApprovedContent ? 'Documentação habilitada' : undefined,
					message: replaceAppealApprovedContent
						? resourceGrantedMessage
							? 'A documentação foi aprovada e habilitada'
							: message.message
						: undefined
				})
			)
		})

		counterarguments.forEach(counterargument => {
			if (counterargument?.description && counterargument?.created) {
				items.push(
					this.toMessageItem({
						id: `counterargument-${counterargument.id}`,
						created: counterargument.created,
						hasAttachment: !!counterargument.principalAttachmentId,
						resourceId: counterargument.principalAttachmentId,
						sourceTag: 'Recurso/Contrarrazão',
						senderType: 'SISTEMA',
						title: counterargument.typeLabel,
						message: counterargument.description
					})
				)
			}

			const judging = counterargument.metadata?.data?.find(it => it.type === SupplierEnrollmentNoticeCounterargumentDataType.JUDGING)

			if (judging?.description && judging?.created) {
				items.push(
					this.toMessageItem({
						id: `counterargument-judging-${counterargument.id}-${judging.id}`,
						created: judging.created,
						hasAttachment: !!judging.id,
						resourceId: judging.id,
						sourceTag: 'Recurso/Contrarrazão',
						senderType: 'AGENTE',
						title: 'Resposta do agente',
						message: judging.description
					})
				)
			}
		})

		return items
			.filter(it => !!it.created)
			.sort((a, b) => moment(a.created).valueOf() - moment(b.created).valueOf())
	}

	private mapRepresentationAnswers(representation: NoticeSessionRepresentation) {
		const items: SupplierEnrollmentNoticeMessageItem[] = []

		;(representation.messages ?? [])
			.filter(it => it.type === NoticeSessionMessageType.REPRESENTATION)
			.forEach(message => {
				if (!message?.message || !message?.created) {
					return
				}

				items.push(
					this.toMessageItem({
						id: `representation-answer-${representation.id}-${message.id}`,
						created: message.created,
						hasAttachment: message.hasAttachment,
						resourceId: message.id,
						sourceTag: 'Diligência',
						senderType: 'PARTICIPANTE',
						title: 'Resposta da diligência',
						message: message.message
					})
				)
			})

		return items
	}

	private findDocumentationAttachment(detail: SupplierEnrollmentNotice): SupplierEnrollmentNoticeAttachment {
		return (detail.attachments ?? []).find(
			it =>
				it.hasAttachment &&
				(it.type === SupplierEnrollmentNoticeAttachmentType.PRINCIPAL ||
					it.type === 2 ||
					it.typeLabel?.toLowerCase()?.includes('habilitação'))
		)
	}

	private toMessageItem(data: {
		id: string
		created: string
		title?: string
		message?: string
		customMessage?: string
		hasAttachment: boolean
		resourceId?: string
		sourceTag: string
		senderType: SupplierEnrollmentNoticeMessageSender
	}): SupplierEnrollmentNoticeMessageItem {
		const senderTag = data.senderType === 'AGENTE' ? 'Agente' : data.senderType === 'PARTICIPANTE' ? 'Participante' : 'Sistema'
		const content = data.customMessage
			? this.formatCustomMessageWithTags(data.customMessage, data.sourceTag, senderTag, data.senderType)
			: this.buildMessageContent(
					data.title ?? 'Mensagem',
					data.message ?? 'Nenhuma observação informada.',
					data.created,
					data.hasAttachment,
					data.sourceTag,
					senderTag,
					data.senderType
				)

		return {
			id: data.id,
			created: data.created,
			hasAttachment: data.hasAttachment,
			customMessage: content,
			resourceId: data.resourceId,
			sourceTag: data.sourceTag,
			senderTag,
			senderType: data.senderType
		}
	}

	private buildMessageContent(
		title: string,
		message: string,
		created: string,
		hasAttachment: boolean,
		sourceTag: string,
		senderTag: string,
		senderType: SupplierEnrollmentNoticeMessageSender
	) {
		const content = `
			${message}
			<br><br>
			<div class="d-flex justify-content-between align-content-center">
				<small class="text-start">${hasAttachment ? 'Baixar o anexo' : ''}</small>
				<b class="text-end">${moment(created).format('DD/MM/YYYY HH:mm')}</b>
			</div>
		`

		return this.wrapMessageWithHeader(title, content, sourceTag, senderTag, senderType)
	}

	private formatCustomMessageWithTags(messageContent: string, sourceTag: string, senderTag: string, senderType: SupplierEnrollmentNoticeMessageSender) {
		const titleRegex = /<b class="text-info">([\s\S]*?)<\/b>/
		const match = messageContent.match(titleRegex)

		if (!match) {
			return this.wrapMessageWithHeader('Mensagem', messageContent, sourceTag, senderTag, senderType)
		}

		const title = match[1]?.trim() || 'Mensagem'
		const content = messageContent.replace(titleRegex, '').replace(/^\s*(<br>\s*){1,2}/, '')

		return this.wrapMessageWithHeader(title, content, sourceTag, senderTag, senderType)
	}

	private wrapMessageWithHeader(
		title: string,
		messageContent: string,
		sourceTag: string,
		senderTag: string,
		senderType: SupplierEnrollmentNoticeMessageSender
	) {
		const senderClass = senderType === 'SISTEMA' ? 'text-bg-primary' : senderType === 'PARTICIPANTE' ? 'text-bg-info' : 'text-bg-indigo'

		return `
			<div class="d-flex justify-content-between align-items-start mb-2">
				<b class="text-info pe-2">${title}</b>
				<div class="d-flex justify-content-end align-items-center flex-wrap gap-1">
					<small class="badge badge-sm text-bg-light border">${sourceTag}</small>
					<small class="badge badge-sm ${senderClass}">${senderTag}</small>
				</div>
			</div>
			${messageContent}
		`
	}
}
